import api, { setAuthToken } from './api';
import { markServerContact } from './offline/connectivity';

const AuthService = {
  async login(email, password, rememberMe = false) {
    console.debug('[AUTH SERVICE] Starting login attempt...', { email, rememberMe });
    // Drop any prior session token BEFORE posting. The request interceptor
    // re-reads authToken from storage on every call, so a leftover token (e.g.
    // an MFA-incomplete shadow session from a previous user) would otherwise
    // ride along on /api/login/ and make the new login fail with a misleading
    // "Unable to connect" error. Logging in as a new user must start clean.
    setAuthToken(null);
    try {
      console.debug('[AUTH SERVICE] Making login request to /api/login/');
      const response = await api.post(
        '/api/login/',
        { email, password, remember_me: rememberMe },
        { withCredentials: true }
      );
      console.debug('[AUTH SERVICE] Raw login response:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data
      });
      
      const { data } = response;
      
      // Validate response format - tokens should always be present now
      if (!data.tokens?.access_token) {
        console.error('[AUTH SERVICE] Invalid login response format - missing tokens:', {
          hasTokens: !!data.tokens,
          hasAccessToken: !!data.tokens?.access_token,
          dataKeys: Object.keys(data)
        });
        throw new Error('Invalid login response format');
      }
      
      console.debug('[AUTH SERVICE] Valid tokens received:', {
        hasTokens: !!data.tokens,
        hasUser: !!data.user,
        userType: data.user_type,
        accessTokenLength: data.tokens.access_token?.length,
        mfaSetupRequired: data.mfa_setup_required,
        mfaVerificationRequired: data.mfa_verification_required,
      });
      
      // CRITICAL: Store tokens IMMEDIATELY, even if MFA is required
      const { tokens, user, user_type } = data;
      console.debug('[AUTH SERVICE] Storing tokens and user data:', { 
        user: user?.email,
        user_type,
        is_internal: user?.is_internal
      });
      
      // Set the access token
      localStorage.setItem('authToken', tokens.access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${tokens.access_token}`;
      
      // Set refresh token if provided
      if (tokens.refresh_token) {
        console.debug('[AUTH SERVICE] Setting refresh token, rememberMe:', rememberMe);
        if (rememberMe) {
          localStorage.setItem('refreshToken', tokens.refresh_token);
        } else {
          sessionStorage.setItem('refreshToken', tokens.refresh_token);
        }
      }

      // Store user info in localStorage
      if (user) {
        console.debug('[AUTH SERVICE] Storing user info:', user);
        localStorage.setItem('user', JSON.stringify(user));
      }
      
      // NOW check for MFA requirements (after tokens are stored)
      if (data.mfa_setup_required) {
        console.debug('[AUTH SERVICE] MFA setup required response:', {
          mfa_setup_required: data.mfa_setup_required,
          mfa_status: data.mfa_status,
          user: data.user?.email
        });
        return {
          ...data, // Include tokens and user data
          mfa_setup_required: true
        };
      }
      
      if (data.mfa_verification_required) {
        console.debug('[AUTH SERVICE] MFA verification required response:', {
          mfa_verification_required: data.mfa_verification_required,
          mfa_status: data.mfa_status,
          user: data.user?.email
        });
        return {
          ...data, // Include tokens and user data
          mfa_required: true
        };
      }
      
      console.debug('[AUTH SERVICE] Login successful, no MFA required');

      console.debug('[AUTH SERVICE] Login process complete');
      return { tokens, user, user_type };
    } catch (error) {
      // Handle 401 unauthorized specifically
      if (error.response?.status === 401) {
        console.error('[AUTH SERVICE] Authentication failed:', error.response?.data);
        
        // Check if this is an account locked error
        if (error.response.data.account_locked) {
          console.error('[AUTH SERVICE] Account locked:', error.response.data.error);
          const lockError = new Error(error.response.data.error);
          lockError.status = error.response.status;
          lockError.data = error.response.data;
          lockError.accountLocked = true;
          throw lockError;
        }
        // Check if this is a password expired error
        else if (error.response.data.password_expired) {
          console.error('[AUTH SERVICE] Password expired:', error.response.data.error);
          const expiredError = new Error(error.response.data.error);
          expiredError.status = error.response.status;
          expiredError.data = error.response.data;
          expiredError.passwordExpired = true;
          throw expiredError;
        } else {
          // Regular invalid credentials
          const authError = new Error('Invalid credentials');
          authError.status = error.response.status;
          authError.data = error.response.data;
          throw authError;
        }
      }
      
      // Handle rate limiting (429) errors specifically
      if (error.response?.status === 429) {
        console.error('[AUTH SERVICE] Rate limited:', error.response?.data);
        const rateLimitError = new Error('Too many login attempts. Please wait a moment and try again.');
        rateLimitError.status = error.response.status;
        rateLimitError.data = error.response.data;
        throw rateLimitError;
      }
      
      // Handle other errors
      console.error('[AUTH SERVICE] Login error:', { 
        status: error.response?.status,
        data: error.response?.data,
        error: error.message 
      });
      
      const networkError = new Error(error.message);
      networkError.status = error.response?.status;
      networkError.data = error.response?.data;
      throw networkError;
    }
  },
  
  // Persist an already-received participant login response (tokens + user) without re-hitting the API.
  // Returns the same normalized shape as participantLogin.
  storeParticipantSession(data, rememberMe = false) {
    if (!data?.tokens?.access_token || !data?.tokens?.refresh_token) {
      throw new Error('Invalid login response format');
    }
    const { access_token, refresh_token } = data.tokens;
    const { user, user_type } = data;

    localStorage.setItem('authToken', access_token);
    api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

    if (rememberMe) {
      localStorage.setItem('refreshToken', refresh_token);
    } else {
      sessionStorage.setItem('refreshToken', refresh_token);
    }

    if (user) {
      // FECOA-1270: user carries effective_offline_cutoff_hours, read back by
      // the offline queue's device-side lock (services/offline/connectivity.js).
      localStorage.setItem('user', JSON.stringify(user));
    }
    markServerContact();

    return { tokens: { access_token, refresh_token }, user, user_type };
  },

  async participantLogin(participantNumber, pin, studyCode, rememberMe = false) {
    console.debug('[AUTH SERVICE] Starting participant login...', {
      participantNumber,
      studyCode,
      rememberMe
    });

    try {
      console.debug('[AUTH SERVICE] Making participant login request...');
      const { data } = await api.post('/api/participant/login/', {
        participant_number: participantNumber,
        study_code: studyCode,
        pin,
    //    remember_me: rememberMe
      });

      console.debug('[AUTH SERVICE] Participant login successful:', {
        user_id: data.user?.id,
        user_type: data.user_type
      });

      return this.storeParticipantSession(data, rememberMe);
    } catch (error) {
      // Handle 401 unauthorized specifically
      if (error.response?.status === 401) {
        console.error('[AUTH SERVICE] Participant authentication failed:', error.response?.data);
        const authError = new Error('Invalid credentials');
        authError.status = error.response.status;
        authError.data = error.response.data;
        throw authError;
      }
      
      // Handle other errors
      console.error('[AUTH SERVICE] Participant login error:', { 
        status: error.response?.status,
        data: error.response?.data,
        error: error.message 
      });
      
      const networkError = new Error(error.message);
      networkError.status = error.response?.status;
      networkError.data = error.response?.data;
      throw networkError;
    }
  },
  
  clearTokens() {
    console.debug('[AUTH SERVICE] Clearing all tokens and user data...');
    try {
      // Clear general auth data
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('selectedSponsor');
      localStorage.removeItem('userType');
      sessionStorage.removeItem('refreshToken');
      
      // Check for participant-specific data before removing user
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          if (userData && (userData.is_participant || userData.token_type === 'participant')) {
            console.debug('[AUTH SERVICE] Clearing participant-specific data');
            // Clear any participant-specific items
            localStorage.removeItem('participantData');
            localStorage.removeItem('participantStudy');
            localStorage.removeItem('participantNumber');
            localStorage.removeItem('participantPin');
            localStorage.removeItem('participantSession');
            // Add any other participant-related storage keys here
          }
        } catch (parseError) {
          console.error('[AUTH SERVICE] Error parsing user data:', parseError);
        }
      }
      
      // Always remove user data after checking its type
      localStorage.removeItem('user');
      
      // Remove the Authorization header
      delete api.defaults.headers.common['Authorization'];
      console.log('[AUTH SERVICE] All tokens and user data cleared');
    } catch (error) {
      console.error('[AUTH SERVICE] Error clearing tokens:', error);
      // Don't throw the error as it might be due to storage restrictions
    }
  },
  
  async logout() {
    console.log('[AUTH SERVICE] Starting logout process...');
    try {
      const currentRefreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
      await api.post('/api/logout/', { 'refresh_token': currentRefreshToken });
      console.log('[AUTH SERVICE] Logout request sent');
    } catch (error) {
      console.error('[AUTH SERVICE] Error during logout request:', error);
    } finally {
      // Always clear tokens regardless of whether the API call succeeds
      this.clearTokens();
      console.log('[AUTH SERVICE] All tokens and user data cleared after logout');
    }
  },
  
  async checkAuthStatus() {
    console.log('[AUTH SERVICE] Starting auth status check...');
    try {
      const authToken = localStorage.getItem('authToken');
      console.log('[AUTH SERVICE] Token exists:', !!authToken);
      
      if (!authToken) {
        console.log('[AUTH SERVICE] No auth token found');
        return { isAuthenticated: false };
      }
      
      console.log('[AUTH SERVICE] Setting auth header and checking status...');
      api.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
      
      const { data } = await api.get('/api/authstatus');
      console.log('[AUTH SERVICE] Auth status response:', {
        data,
        user: data.user,
        is_internal: data.user?.is_internal,
        is_internal_type: typeof data.user?.is_internal
      });
      
      if (!data.isAuthenticated) {
        // Don't clear tokens if MFA is in progress
        if (data.mfaRequired || data.mfaSetupRequired) {
          console.log('[AUTH SERVICE] Not authenticated but MFA in progress, keeping tokens for verification');
        } else {
          console.log('[AUTH SERVICE] Not authenticated, clearing tokens');
          this.clearTokens();
        }
      }
      
      return data;
    } catch (error) {
      console.error('[AUTH SERVICE] Auth status check error:', { 
        status: error.response?.status,
        data: error.response?.data,
        error: error.message 
      });

      // If we're being rate limited, don't clear tokens.
      // Clearing tokens turns a transient 429 into a logout + redirect loop,
      // which can amplify request volume and worsen throttling.
      if (error?.response?.status === 429) {
        return { isAuthenticated: false, rate_limited: true };
      }

      this.clearTokens();
      return { isAuthenticated: false };
    }
  },
  
  async getUserInfo() {
    console.log('[AUTH SERVICE] Getting user info...');
    try {
      const { data } = await api.get('/api/user/info/');
      console.log('[AUTH SERVICE] User info response:', data);
      
      // Store updated user info in localStorage
      if (data.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
      }
      return data;
    } catch (error) {
      console.error('[AUTH SERVICE] Error getting user info:', { 
        status: error.response?.status,
        data: error.response?.data,
        error: error.message 
      });
      throw error;
    }
  },
  
  async validateActivationToken(uid, token) {
    console.log('[AUTH SERVICE] Validating activation token...', { uid, token });
    const { data } = await api.get(`/api/activate/${uid}/${token}/`);
    console.log('[AUTH SERVICE] Activation token validation response:', data);
    return data;
  },
  
  async activateAccount(uid, token, password) {
    console.log('[AUTH SERVICE] Activating account...', { uid, token });
    const { data } = await api.post(`/api/activate/${uid}/${token}/`, { password });
    console.log('[AUTH SERVICE] Account activation response:', data);
    return data;
  },
  
  async refreshToken() {
    console.log('[AUTH SERVICE] Refreshing token...');
    try {
      const currentRefreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
      
      if (!currentRefreshToken) {
        console.error('[AUTH SERVICE] No refresh token available');
        this.clearTokens();
        throw new Error('No refresh token available');
      }

      const { data } = await api.post('/api/token/refresh/', {
        refresh: currentRefreshToken
      });
      
      if (!data.tokens?.access_token) {
        console.error('[AUTH SERVICE] Invalid response from refresh token endpoint');
        this.clearTokens();
        throw new Error('Invalid response from refresh token endpoint');
      }
      
      // Log refresh response for debugging
      console.log('[AUTH SERVICE] Token refresh response:', { 
        user_type: data.user_type,
        tokens: !!data.tokens,
        user: !!data.user
      });
      
      // Set the new access token
      localStorage.setItem('authToken', data.tokens.access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${data.tokens.access_token}`;
      
      // Store refresh token in the same storage it was retrieved from
      if (data.tokens.refresh_token) {
        if (localStorage.getItem('refreshToken')) {
          localStorage.setItem('refreshToken', data.tokens.refresh_token);
        } else if (sessionStorage.getItem('refreshToken')) {
          sessionStorage.setItem('refreshToken', data.tokens.refresh_token);
        }
      }

      // Store updated user info
      if (data.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
      }
      
      // Store user type
      if (data.user_type) {
        localStorage.setItem('userType', data.user_type);
      }

      // For regular users, update selected sponsor if it changed
      if (data.user_type === 'user' && data.user?.sponsor_id) {
        const currentSponsor = JSON.parse(localStorage.getItem('selectedSponsor') || '{}');
        if (currentSponsor.id !== data.user.sponsor_id) {
          localStorage.setItem('selectedSponsor', JSON.stringify({
            id: data.user.sponsor_id,
            // Keep the existing name if we have it
            name: currentSponsor.name || 'Unknown Sponsor'
          }));
        }
      }
      
      console.log('[AUTH SERVICE] Token refresh successful');
      return data;
    } catch (error) {
      console.error('[AUTH SERVICE] Error refreshing token:', { 
        status: error.response?.status,
        data: error.response?.data,
        error: error.message 
      });
      this.clearTokens();
      throw error;
    }
  },

  async requestMFARecovery() {
    console.log('[AUTH SERVICE] Requesting MFA recovery for authenticated user');
    
    try {
      const response = await api.post('/api/mfa/recovery/request/', {});
      
      console.log('[AUTH SERVICE] MFA recovery request successful');
      return response.data;
    } catch (error) {
      console.error('[AUTH SERVICE] MFA recovery request failed:', error);
      throw error;
    }
  },

  async requestMFARecoveryByEmail(email) {
    console.log('[AUTH SERVICE] Requesting MFA recovery by email:', email);
    
    try {
      const response = await api.post('/api/mfa/recovery/request/', {
        email: email
      });
      
      console.log('[AUTH SERVICE] MFA recovery request by email successful');
      return response.data;
    } catch (error) {
      console.error('[AUTH SERVICE] MFA recovery request by email failed:', error);
      throw error;
    }
  },

  async verifyMFARecoveryToken(token) {
    console.log('[AUTH SERVICE] Verifying MFA recovery token');
    
    try {
      const response = await api.get(`/api/mfa/recovery/verify/${token}/`);
      console.log('[AUTH SERVICE] MFA recovery token verified');
      return response.data;
    } catch (error) {
      console.error('[AUTH SERVICE] MFA recovery token verification failed:', error);
      throw error;
    }
  },

  async executeMFARecovery(token, action) {
    console.log('[AUTH SERVICE] Executing MFA recovery action:', action);
    
    try {
      const response = await api.post(`/api/mfa/recovery/verify/${token}/`, {
        action: action
      });
      
      console.log('[AUTH SERVICE] MFA recovery executed successfully');
      return response.data;
    } catch (error) {
      console.error('[AUTH SERVICE] MFA recovery execution failed:', error);
      throw error;
    }
  }
};

export default AuthService;
